import ApiService from '@/common/api.service'

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param note Object
 *
 * @return Object
 */
function transform (note) {
  return {
    id: note.id,
    note: note.note
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
export function defaults () {
  return {
    isEditing: false,
    isAdding: false,
    isDeleting: false,
    warning: '',
    notes: []
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns an array of notes
   *
   * @return Array
   */
  notes (state) {
    return state.notes
  },

  /**
   * Returns the current form warning.
   *
   * @param state Object
   *
   * @return string
   */
  warning (state) {
    return state.warning
  }
}

export const actions = {
  /**
   * API request to create a new note. The payload must be in the format:
   * { url: String, data: Object }
   *
   * @return Promise
   */
  post (context, payload) {
    return ApiService
      .post(payload.url, transform(payload.data))
      .then((response) => {
        context.commit('add', response.data.data)
        return response
      })
  }
}

export const mutations = {
  /**
   * Set an array of notes.
   *
   * @param state Object
   * @param notes Array
   */
  setNotes (state, notes) {
    state.notes = notes
  },

  /**
   * Add a new note.
   *
   * @param state Object
   * @param note  Object
   */
  add (state, note) {
    state.notes.push(note)
  },

  /**
   * Find a note by index in the array of notes and updates it.
   *
   * @param state   Object
   * @param note Object
   * @param index   Number
   */
  updateByIndex (state, { note, index }) {
    state.notes[index] = note
  },

  /**
   * Updates the last note that was added.
   *
   * @param state   Object
   * @param note Object
   */
  updateNewest (state, note) {
    state.notes[state.notes.length - 1] = note
  },

  /**
   * Find a note by id and remove it.
   *
   * @param state Object
   * @param id    Number
   */
  removeById (state, id) {
    let index = state.notes.findIndex(element => element.id === id)
    state.notes.splice(index, 1)
  },

  /**
   * Remove a note by index in the array of notes.
   *
   * @param state Object
   * @param index Number
   */
  removeByIndex (state, index) {
    state.notes.splice(index, 1)
  },

  /**
   * Clears the notes from the state.
   *
   * @param state Object
   */
  purge (state) {
    state.notes = []
  },

  /**
   * Sets the isAdding form state.
   *
   * @param state   Object
   * @param isAdding Boolean
   */
  setIsAdding (state, isAdding) {
    state.isAdding = isAdding
  },

  /**
   * Sets the isEditing form state.
   *
   * @param state     Object
   * @param isEditing Boolean
   */
  setIsEditing (state, isEditing) {
    state.isEditing = isEditing
  },

  /**
   * Sets the isDeleting form state.
   *
   * @param state      Object
   * @param isDeleting Boolean
   */
  setIsDeleting (state, isDeleting) {
    state.isDeleting = isDeleting
  },

  /**
   * Sets the form warning.
   *
   * @param state      Object
   * @param warning Boolean
   */
  setWarning (state, warning) {
    state.warning = warning
  },

  /**
   * Resets the form's state.
   *
   * @param state      Object
   */
  resetFormState (state) {
    state.isAdding = false
    state.isEditing = false
    state.isDeleting = false
    state.warning = ''
  }
}

export default {
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/notes/notesModule.js